// Smart-Search-Scoring fuer die TopBar-Suche. Pro Item ein Bundle
// aus gewichteten Haystacks (Label 3x, Aliase 2x, Detail 1x,
// Intent 1x) und einem Schwellwert pro Token (alle Tokens MUESSEN
// matchen, sonst
// gibt es keinen Hit). Match-Stufen pro Wort:
//   exakt > Prefix > Substring > Fuzzy (Damerau-Levenshtein <= 1
//   ab 4 Zeichen Tokenlaenge).
// Acronyme werden separat behandelt: kurze Tokens (1-3 Zeichen)
// matchen auch gegen die Anfangsbuchstaben aller Wort-Stuecke ("ac"
// findet "AC Infinity", "z2m" findet "Zigbee2MQTT" -- erst nach
// Slug-Normalisierung).
//
// Das alte Verhalten "Substring auf zusammengefuegtem Haystack" wird
// damit ersetzt -- siehe filterFlat() unten als Drop-in fuer
// QuickSwitcher/TopBar.

export interface Scorable {
  label: string;
  aliases?: string[];
  detail?: string;
  // intent: natuerlichsprachige "was man hier tut"-Zeile mit Verben
  // (z.B. "OpenWrt-Image bauen und flashen"). Unsichtbar in der UI,
  // nur fuer die Suche -- gibt Absichts-Queries ("...bauen") einen
  // Fuzzy-Anker auf der Zielseite, wenn der Inferenzdienst down ist. Gewicht 1x
  // (wie Detail), damit ein echter Namens-Treffer nie ueberholt wird.
  intent?: string;
}

const WORD_RE = /[\p{L}\p{N}]+/gu;

function words(s: string): string[] {
  if (!s) return [];
  return (s.toLowerCase().match(WORD_RE) ?? []).filter(Boolean);
}

// Damerau-Levenshtein optimiert fuer max-Distanz <=1. Wir geben true
// zurueck wenn die Distanz hoechstens 1 ist. Damit sparen wir uns das
// volle DP-Array fuer die >99% der Fragen.
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  const diff = la - lb;
  if (diff > 1 || diff < -1) return false;
  // Substitution-Check (gleiche Laenge)
  if (la === lb) {
    let mismatches = 0;
    for (let i = 0; i < la; i++) {
      if (a[i] !== b[i]) {
        mismatches++;
        // Transposition: ab -> ba zaehlt als 1 Edit, nicht 2.
        if (
          mismatches === 1 &&
          i + 1 < la &&
          a[i] === b[i + 1] &&
          a[i + 1] === b[i]
        ) {
          i++;
          continue;
        }
        if (mismatches > 1) return false;
      }
    }
    return true;
  }
  // Insertion/Deletion: pruefe ob die kuerzere als Teilfolge in der
  // laengeren mit einer Lueche steckt.
  const [shorter, longer] = la < lb ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i++;
      j++;
    } else {
      if (skipped) return false;
      skipped = true;
      j++;
    }
  }
  return true;
}

// scoreToken vergleicht einen einzelnen Such-Token gegen eine Wort-
// Liste und gibt den hoechsten Treffer-Score zurueck (0 = kein Treffer).
// Match-Stufen:
//   10 -- exakter Wort-Match
//    6 -- Wort-Prefix-Match
//    4 -- Wort enthaelt Token als Substring
//    2 -- Damerau-Levenshtein <= 1 (nur bei tok.length >= 4)
function scoreToken(tok: string, words: string[]): number {
  let best = 0;
  const fuzzy = tok.length >= 4;
  for (const w of words) {
    if (w === tok) {
      if (best < 10) best = 10;
      // Hoehere Stufe kann nicht mehr kommen, frueh raus.
      if (best === 10 && !fuzzy) break;
    } else if (w.startsWith(tok)) {
      if (best < 6) best = 6;
    } else if (w.includes(tok)) {
      if (best < 4) best = 4;
    } else if (fuzzy && Math.abs(w.length - tok.length) <= 1 && withinOneEdit(tok, w)) {
      if (best < 2) best = 2;
    }
  }
  return best;
}

// Akronym: bei kurzen Tokens (1-3 Zeichen) versuchen wir, sie als
// Anfangsbuchstaben der Wort-Liste zu lesen. "ac" + ["ac", "infinity"]
// -> matcht. Score liegt zwischen Prefix (6) und Substring (4), damit
// "Akku" nicht ueber "AC Infinity" rutscht.
function scoreAcronym(tok: string, words: string[]): number {
  if (tok.length < 2 || tok.length > 4) return 0;
  if (words.length < tok.length) return 0;
  // Suche eine zusammenhaengende Sequenz von Woertern, deren
  // Anfangsbuchstaben das Token bilden.
  outer: for (let start = 0; start + tok.length <= words.length; start++) {
    for (let i = 0; i < tok.length; i++) {
      if (words[start + i][0] !== tok[i]) continue outer;
    }
    return 5;
  }
  return 0;
}

interface Cache {
  label: string[];
  aliases: string[];
  detail: string[];
  intent: string[];
}

const CACHE = new WeakMap<Scorable, Cache>();

function getCache(item: Scorable): Cache {
  let c = CACHE.get(item);
  if (!c) {
    c = {
      label: words(item.label),
      aliases: words((item.aliases ?? []).join(" ")),
      detail: words(item.detail ?? ""),
      intent: words(item.intent ?? ""),
    };
    CACHE.set(item, c);
  }
  return c;
}

// scoreItem: kombiniert die Tokens. Alle Tokens MUESSEN treffen
// (UND-Verknuepfung), sonst null. Pro Token gewinnt der hoechste
// Stufen-Score aus den vier Haystacks (Label 3x, Aliase 2x, Detail 1x,
// Intent 1x).
export function scoreItem(item: Scorable, tokens: string[]): number | null {
  const c = getCache(item);
  let total = 0;
  for (const tok of tokens) {
    const labelScore = scoreToken(tok, c.label) * 3;
    const aliasScore = scoreToken(tok, c.aliases) * 2;
    const detailScore = scoreToken(tok, c.detail) * 1;
    const intentScore = scoreToken(tok, c.intent) * 1;
    const acronymScore = scoreAcronym(tok, c.label) * 3;
    const best = Math.max(labelScore, aliasScore, detailScore, intentScore, acronymScore);
    if (best === 0) return null;
    total += best;
  }
  return total;
}

// filterAndRank: Hauptfunktion fuer Konsumenten. Tokenisiert die
// Query, scored alle Items und sortiert absteigend.
//
// Optionale opts:
//   getBonus(item): zusaetzlicher Score-Bonus pro Item (z.B. aus dem
//     adaptiven Lern-Layer search-learn). Wird auf den Fuzzy-Score
//     addiert -- Caller (search-learn) ist dafuer verantwortlich,
//     den Bonus selbst zu deckeln.
//   getRecency(item): hoeher = frischer; nur als Tiebreaker zwischen
//     gleichem (score+bonus). Standard-Tiebreak bleibt label.length
//     aufsteigend (so dass "Tasks" vor "Task-Settings" landet), wird
//     aber bei vorhandenem getRecency nach hinten geschoben.
export function filterAndRank<T extends Scorable>(
  items: T[],
  query: string,
  optsOrLimit?: number | {
    limit?: number;
    getBonus?: (item: T) => number;
    getRecency?: (item: T) => number;
  },
): T[] {
  const opts = typeof optsOrLimit === "number" ? { limit: optsOrLimit } : (optsOrLimit ?? {});
  const limit = opts.limit ?? 50;
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const tokens = q.split(/\s+/).filter(Boolean);
  const hits: { item: T; score: number; recency: number }[] = [];
  for (const it of items) {
    const s = scoreItem(it, tokens);
    if (s === null) continue;
    const bonus = opts.getBonus ? opts.getBonus(it) : 0;
    const recency = opts.getRecency ? opts.getRecency(it) : 0;
    hits.push({ item: it, score: s + bonus, recency });
  }
  hits.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.recency !== a.recency) return b.recency - a.recency;
    return a.item.label.length - b.item.label.length;
  });
  return hits.slice(0, limit).map((h) => h.item);
}
